import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Twitter, Youtube, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Footer = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter/signup`, { email });
      toast.success("You're in — keep an eye out for new drops & private sales.");
      setEmail('');
    } catch (error) {
      if (error.response?.data?.detail === 'Email already subscribed') {
        toast.success("You're already on the list.");
      } else {
        toast.error('Failed to subscribe. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const footerLinks = {
    shop: [
      { name: 'All Fragrances', path: '/products' },
    ],
    support: [
      { name: 'FAQ', path: '/faq' },
      { name: 'Shipping & Returns', path: '/shipping-returns' },
    ],
  };

  const socialLinks = [
    { icon: Instagram, href: '#', label: 'Instagram' },
    { icon: Facebook, href: '#', label: 'Facebook' },
    { icon: Twitter, href: '#', label: 'Twitter' },
    { icon: Youtube, href: '#', label: 'Youtube' },
  ];

  return (
    <footer className="bg-[#050505] pt-20 pb-8 border-t border-[#D4AF37]/20" data-testid="main-footer">
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        {/* Newsletter Section */}
        <div className="border-b border-[#D4AF37]/20 pb-16 mb-16">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="font-arabic text-2xl text-[#D4AF37]/60 mb-2">انضم إلينا</p>
              <h3 className="font-heading text-3xl md:text-4xl tracking-wide text-white mb-4">
                JOIN OUR <span className="text-[#D4AF37]">WORLD</span>
              </h3>
              <p className="font-body text-white/60 text-sm leading-relaxed">
                Subscribe to receive exclusive updates, early access to new collections, 
                and invitations to private events.
              </p>
            </div>
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 min-w-0">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                data-testid="newsletter-email-input"
                className="flex-1 min-w-0 bg-transparent border-b border-[#D4AF37]/30 focus:border-[#D4AF37] text-white placeholder:text-white/30 py-3 outline-none font-body"
                required
              />
              <button
                type="submit"
                data-testid="newsletter-submit-button"
                disabled={loading}
                className="bg-[#D4AF37] text-[#050505] px-6 py-3 font-heading text-xs tracking-[0.2em] hover:bg-[#E5C158] transition-colors duration-200 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? 'JOINING...' : 'SUBSCRIBE'}
                <ArrowRight size={16} />
              </button>
            </form>
          </div>
        </div>

        {/* Links Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {/* Brand Column */}
          <div>
            <Link to="/" className="inline-block mb-6">
              <h2 className="text-2xl tracking-[0.1em] text-white" style={{ fontFamily: "'Aref Ruqaa', serif", fontWeight: 700 }}>
                QASR <span className="text-[#D4AF37]">PARFUMERIE</span>
              </h2>
            </Link>
            <p className="font-body text-white/50 text-xs leading-relaxed mb-6">
              Crafting extraordinary fragrances inspired by Arabian heritage.
            </p>
            <div className="flex space-x-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  data-testid={`social-link-${social.label.toLowerCase()}`}
                  className="w-10 h-10 border border-[#D4AF37]/30 flex items-center justify-center text-[#D4AF37]/50 hover:text-[#D4AF37] hover:border-[#D4AF37] transition-colors duration-200"
                  aria-label={social.label}
                >
                  <social.icon size={16} />
                </a>
              ))}
            </div>
          </div>

          {/* Shop Links */}
          <div>
            <h4 className="font-heading text-xs tracking-[0.2em] text-[#D4AF37] mb-6">SHOP</h4>
            <ul className="space-y-3">
              {footerLinks.shop.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="font-body text-white/50 text-xs hover:text-[#D4AF37] transition-colors duration-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* About Links section removed */}

          {/* Support Links */}
          <div>
            <h4 className="font-heading text-xs tracking-[0.2em] text-[#D4AF37] mb-6">SUPPORT</h4>
            <ul className="space-y-3">
              {footerLinks.support.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="font-body text-white/50 text-xs hover:text-[#D4AF37] transition-colors duration-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-[#D4AF37]/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="font-body text-white/30 text-xs">
            © 2024 Qasr Parfumerie. All rights reserved.
          </p>
          <p className="font-arabic text-[#D4AF37]/30 text-sm">عطور فاخرة</p>
          <div className="flex space-x-6">
            <Link to="/contact" className="font-body text-white/30 text-xs hover:text-[#D4AF37]/60 transition-colors duration-200">
              Privacy Policy
            </Link>
            <Link to="/contact" className="font-body text-white/30 text-xs hover:text-[#D4AF37]/60 transition-colors duration-200">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
